// src/components/TaskListSection/DynamicComponentTodoList/ExpertSelection.js
import React, { useEffect, useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ExpertCart from "./ExpertCart";
import { useDispatch } from "react-redux";
import {
    getAllInspector,
    fetchInspectorsByAssociatedParty,
    getInspectorsByGarageAndDate,
} from "../../../store/action/inspectionMission";

const useStyles = makeStyles(() => ({
    inputContainer: {
        border: "0.61px solid rgba(226, 217, 239, 1)",
        background: "#fff",
        width: 200,
        height: 36,
        borderRadius: 5,
        marginLeft: 20,
        padding: "0 8px",
    },
    titre: {
        fontFamily: "Poppins",
        fontSize: 16,
        fontWeight: 600,
        color: "rgba(63, 66, 84, 1)",
    },
    titreLabel: {
        fontFamily: "Poppins",
        fontSize: 14,
        fontWeight: 400,
        color: "rgba(32, 18, 70, 1)",
    },
    loadingText: { fontSize: 16, color: "gray" },
    noRepairersText: { fontSize: 16, color: "red" },
    cardContainer: {
        display: "flex",
        flexWrap: "wrap",
        justifyContent: "space-between",
    },
    smallBtn: {
        border: "0.61px solid rgba(226,217,239,1)",
        background: "rgb(60, 101, 86)",
        borderRadius: 6,
        padding: "6px 10px",
        cursor: "pointer",
        fontFamily: "Poppins",
        fontSize: 12,
        color: "#ffff",
        width: "111px",
        height: "30px",
    },
    topBar: {
        display: "flex",
        alignItems: "center",
        margin: 20,
        gap: 16,
        flexWrap: "wrap",
    },
}));

/**
 * Props:
 *  - designatedExperts(inspector) : callback when user selects an inspector
 *  - designatedRepairerPartyUid  : optional, if provided we enrich with inspectors linked to the garage/date
 *
 * NOTE: Any `filters` prop will be ignored on purpose (we always call getAllInspector()).
 */
const ExpertSelector = ({ designatedExperts, designatedRepairerPartyUid }) => {
    const classes = useStyles();
    const dispatch = useDispatch();

    const [selectedInspector, setSelectedInspector] = useState(null);
    const [loading, setLoading] = useState(false);
    const [inspectors, setInspectors] = useState([]);

    // quick client-side filters (text inputs)
    const [nomFiltre, setNomFiltre] = useState("");
    const [regionFiltre, setRegionFiltre] = useState("");
    const [gouvernoratFiltre, setGouvernoratFiltre] = useState("");

    const handleInspectorSelect = (inspector) => {
        designatedExperts?.(inspector);
        setSelectedInspector(inspector);
    };

    const clearQuickFilters = () => {
        setNomFiltre("");
        setRegionFiltre("");
        setGouvernoratFiltre("");
    };

    // Load *all* inspectors once, then optionally merge garage-related inspectors
    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                // 1) get all inspectors
                let all = await dispatch(getAllInspector());
                if (!Array.isArray(all)) all = [];

                // 2) optional: merge with garage/date inspectors (marked ToPlan = true)
                if (designatedRepairerPartyUid) {
                    try {
                        const timeslot = await getInspectorsByGarageAndDate(designatedRepairerPartyUid);
                        if (Array.isArray(timeslot) && timeslot.length > 0) {
                            const enriched = [];
                            for (const t of timeslot) {
                                // eslint-disable-next-line no-await-in-loop
                                const d = await dispatch(fetchInspectorsByAssociatedParty(t.associatedInspector));
                                if (Array.isArray(d) && d.length) {
                                    enriched.push({ ...d[0], ToPlan: true });
                                }
                            }
                            if (enriched.length) {
                                const map = new Map();
                                [...enriched, ...all].forEach((ins) => map.set(ins._id, ins));
                                all = Array.from(map.values());
                            }
                        }
                    } catch {
                        // ignore garage errors; we still show all inspectors
                    }
                }

                setInspectors(all);
            } catch {
                setInspectors([]);
            } finally {
                setLoading(false);
            }
        };

        load();
    }, [dispatch, designatedRepairerPartyUid]);

    // client-side filtering only (name/region/gouvernorat)
    const filtered = useMemo(() => {
        let out = inspectors;

        if (nomFiltre) {
            const q = nomFiltre.toLowerCase();
            out = out.filter(
                (e) =>
                    e?.personDetails?.name?.toLowerCase().includes(q) ||
                    e?.personDetails?.lastName?.toLowerCase().includes(q)
            );
        }

        if (regionFiltre) {
            const q = regionFiltre.toLowerCase();
            out = out.filter((e) =>
                (e?.inspectorCaracteristiques || []).some(
                    (c) => c?.key === "REGION" && (c?.value || "").toLowerCase().includes(q)
                )
            );
        }

        if (gouvernoratFiltre) {
            const q = gouvernoratFiltre.toLowerCase();
            out = out.filter((e) =>
                (e?.inspectorCaracteristiques || []).some(
                    (c) => c?.key === "GOUVERNORAT" && (c?.value || "").toLowerCase().includes(q)
                )
            );
        }

        return out;
    }, [inspectors, nomFiltre, regionFiltre, gouvernoratFiltre]);

    return (
        <div>
            <div className={classes.topBar}>
                <div className={classes.titre}>Filtrer par</div>

                <div className={classes.titreLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    Nom
                    <input
                        type="text"
                        className={classes.inputContainer}
                        onChange={(e) => setNomFiltre(e.target.value)}
                        value={nomFiltre}
                        aria-label="Nom Expert"
                    />
                </div>

                <div className={classes.titreLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    Gouvernorat
                    <input
                        type="text"
                        className={classes.inputContainer}
                        onChange={(e) => setGouvernoratFiltre(e.target.value)}
                        value={gouvernoratFiltre}
                        aria-label="Gouvernorat Expert"
                    />
                </div>

                <div className={classes.titreLabel} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    Région
                    <input
                        type="text"
                        className={classes.inputContainer}
                        onChange={(e) => setRegionFiltre(e.target.value)}
                        value={regionFiltre}
                        aria-label="Région Expert"
                    />
                </div>

                <button className={classes.smallBtn} onClick={clearQuickFilters}>
                    Effacer Tout
                </button>

                <div style={{ marginLeft: "auto", fontFamily: "Poppins", fontSize: 12 }}>
                    {loading ? "Chargement…" : `${filtered.length} inspecteur(s)`}
                </div>
            </div>

            <div className={classes.cardContainer}>
                {loading ? (
                    <p className={classes.loadingText}>Chargement des inspecteurs...</p>
                ) : filtered?.length > 0 ? (
                    filtered.map((card, index) => (
                        <ExpertCart
                            key={index}
                            filteredInspectors={card}
                            width={"30%"}
                            height={"Auto"}
                            designated={() => handleInspectorSelect(card)}
                            selectedInspector={selectedInspector}
                        />
                    ))
                ) : (
                    <p className={classes.noRepairersText}>Aucun inspecteur disponible</p>
                )}
            </div>
        </div>
    );
};

export default ExpertSelector;
